import {
  Alert,
  Avatar,
  Button,
  FileButton,
  FocusTrap,
  Stack,
} from '@mantine/core'
import classes from './Onboarding.module.css'
import { Typography } from '../common/typography'
import ProfileFieldInput from './ProfileFieldInput'
import { ReactComponent as LeaguestarLogo } from '../assets/leaguestar_logo_color.svg'
import { useEffect, useState } from 'react'
import { IconUpload, IconUser } from '@tabler/icons-react'
import { ProfileField } from './api'

interface ProgressBarProps {
  currentStep: number
  totalSteps: number
}

function ProgressBar({ currentStep, totalSteps }: ProgressBarProps) {
  const targetProgress = (currentStep / totalSteps) * 100
  const [animatedProgress, setAnimatedProgress] = useState(0)

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedProgress(targetProgress)
    }, 50)
    return () => clearTimeout(timer)
  }, [targetProgress])

  return (
    <div className={classes.progressContainer}>
      <div
        className={classes.progressBar}
        style={{
          width: `${animatedProgress}%`,
        }}
      />
    </div>
  )
}

interface OnboardingContentProps {
  currentStep: number
  totalSteps: number
  avatarPreview: string | null
  isUploadingAsset: boolean
  handleAvatarUpload: (file: File | null) => void
  profileSuccess: string | null
  profileError: string | null
  setProfileSuccess: (message: string | null) => void
  setProfileError: (message: string | null) => void
  handleNext: () => void
  sortedProfileFields: ProfileField[]
  fieldValues: Record<string, string | number | boolean>
  handleFieldChange: (key: string, value: string | number | boolean) => void
  fieldErrors: Set<string>
  isUpdatingSettings: boolean
  handleFinish: () => void
}

export default function OnboardingContent({
  currentStep,
  totalSteps,
  avatarPreview,
  isUploadingAsset,
  handleAvatarUpload,
  profileSuccess,
  profileError,
  setProfileSuccess,
  setProfileError,
  handleNext,
  sortedProfileFields,
  fieldValues,
  handleFieldChange,
  fieldErrors,
  isUpdatingSettings,
  handleFinish,
}: OnboardingContentProps) {
  return (
    <>
      <FocusTrap.InitialFocus />
      {currentStep === 1 ? (
        <Typography fs="lg" lh="125" fw={500} ta="center">
          Welcome to Leaguestar
        </Typography>
      ) : (
        <Typography fs="lg" lh="125" fw={500} ta="start" tt="capitalize">
          Profile setup
        </Typography>
      )}
      <div className={classes.centeredContainer}>
        <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
      </div>

      {currentStep === 1 && (
        <div className={classes.container}>
          <div className={classes.centeredContainer}>
            <LeaguestarLogo className={classes.logo} />
          </div>
          <Typography fs="md" lh="140" ta="center">
            In the next few steps we are going to guide you to setup your
            profile.
          </Typography>
          <div className={classes.centeredContainer}>
            <Button className={classes.button} onClick={handleNext}>
              <Typography
                fs="md"
                fc={{ light: 'white', dark: 'white' }}
                lh="150"
                tt="uppercase"
              >
                Next
              </Typography>
            </Button>
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <div className={classes.container}>
          <div className={classes.centeredContainer}>
            <Stack align="center" gap="sm">
              <Avatar
                src={avatarPreview}
                alt="Profile photo"
                className={classes.avatar}
              >
                <IconUser size={50} />
              </Avatar>
              <Stack gap="xxs">
                <FileButton
                  onChange={handleAvatarUpload}
                  accept="image/png,image/jpeg"
                  disabled={isUploadingAsset}
                >
                  {(props) => (
                    <Button
                      {...props}
                      className={classes.button}
                      leftSection={<IconUpload size={16} />}
                      loading={isUploadingAsset}
                    >
                      <Typography
                        lh="150"
                        fc={{ light: 'white', dark: 'white' }}
                      >
                        Upload Photo
                      </Typography>
                    </Button>
                  )}
                </FileButton>
                <Typography
                  fs="xs"
                  ta="center"
                  lh="150"
                  fc={{ light: 'gray-6', dark: 'gray-5' }}
                >
                  Accepted formats: PNG, JPEG
                </Typography>
              </Stack>
            </Stack>
          </div>
          {profileSuccess && (
            <Alert
              color="green"
              mb="md"
              onClose={() => setProfileSuccess(null)}
              withCloseButton
            >
              {profileSuccess}
            </Alert>
          )}
          {profileError && (
            <Alert
              color="red"
              mb="md"
              onClose={() => setProfileError(null)}
              withCloseButton
            >
              {profileError}
            </Alert>
          )}
          <div className={classes.centeredContainer}>
            <Button className={classes.button} onClick={handleNext}>
              <Typography
                fs="md"
                fc={{ light: 'white', dark: 'white' }}
                lh="150"
                tt="uppercase"
              >
                Next
              </Typography>
            </Button>
          </div>
        </div>
      )}

      {currentStep === 3 && (
        <div className={classes.container}>
          <Stack gap="md">
            {sortedProfileFields.map((profileField) => (
              <ProfileFieldInput
                key={profileField.key}
                profileField={profileField}
                value={fieldValues[profileField.key]}
                onChange={handleFieldChange}
                error={fieldErrors.has(profileField.key)}
              />
            ))}
          </Stack>
          {profileSuccess && (
            <Alert
              color="green"
              mb="md"
              onClose={() => setProfileSuccess(null)}
              withCloseButton
            >
              {profileSuccess}
            </Alert>
          )}
          {profileError && (
            <Alert
              color="red"
              mb="md"
              onClose={() => setProfileError(null)}
              withCloseButton
            >
              {profileError}
            </Alert>
          )}
          <div className={classes.centeredContainer}>
            <Button
              className={classes.button}
              loading={isUpdatingSettings}
              onClick={handleFinish}
            >
              <Typography
                fs="md"
                fc={{ light: 'white', dark: 'white' }}
                lh="150"
                tt="uppercase"
              >
                Finish
              </Typography>
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
